import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Star, Award, Heart, Users, Quote, ExternalLink } from 'lucide-react';
import { vendorSuccessStoriesService } from '../../services/vendorSuccessStoriesService';
import { useServiceCategories } from '../../services/serviceCategoryService';
import StoryDetailModal, { QUOTE_MAX_LENGTH } from '../../components/client/StoryDetailModal';

const DEFAULT_ICONS = { Award, Heart, Users };

const VendorSuccessStories = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStory, setSelectedStory] = useState(null);

  const { data: categoriesResponse } = useServiceCategories({ per_page: 50 });

  const { data: response, isLoading } = useQuery({
    queryKey: ['vendorSuccessStories'],
    queryFn: () => vendorSuccessStoriesService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const tabs = useMemo(() => {
    const categories = categoriesResponse?.data?.data || categoriesResponse?.data || [];
    const base = [{ id: 'all', label: 'All Stories', icon: Trophy }];
    const categoryTabs = Array.isArray(categories)
      ? categories
          .filter((c) => c && (c.is_active !== false))
          .map((c, i) => ({
            id: (c.category_name || c.id || '').toLowerCase(),
            label: c.category_name || c.id,
            icon: DEFAULT_ICONS[Object.keys(DEFAULT_ICONS)[i % 3]] || Award,
          }))
      : [];
    return [...base, ...categoryTabs];
  }, [categoriesResponse]);

  const currentStories = useMemo(() => {
    const allStories = response?.data || [];
    if (activeTab === 'all') return allStories;
    return allStories.filter(
      (s) => s.category?.toLowerCase() === activeTab
    );
  }, [response?.data, activeTab]);

  const truncateQuote = (quote) => {
    if (!quote) return '';
    if (quote.length <= QUOTE_MAX_LENGTH) return quote;
    return quote.slice(0, QUOTE_MAX_LENGTH).trim() + '...';
  };

  return (
    <div className="min-h-screen bg-gray-50 -mt-16">
      {/* Hero Section - extends behind transparent header */}
      <div className="bg-gradient-to-br from-black to-gray-900 text-white pt-16 pb-12 sm:pt-20 sm:pb-16">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-white/10 backdrop-blur-sm rounded-full mb-4 sm:mb-6">
              <Trophy className="w-6 h-6 sm:w-8 sm:h-8 animate-bounce-gentle" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              Vendor Success Stories
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-300">
              Discover how vendors are growing their businesses with VendaList
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-container py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Tabs Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 sm:mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex overflow-x-auto -mb-px px-2 sm:px-4 md:px-6 scrollbar-hide">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative py-3 sm:py-4 px-2 sm:px-3 md:px-6 font-medium text-xs sm:text-sm md:text-base transition-all duration-300 whitespace-nowrap border-b-2 flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${
                        activeTab === tab.id
                          ? "text-black border-black"
                          : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                      }`}
                    >
                      <IconComponent className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 flex-shrink-0" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Stories Grid */}
            <div className="p-3 sm:p-4 md:p-6 lg:p-8">
              {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-gray-100 rounded-xl h-80 animate-pulse" />
                  ))}
                </div>
              ) : currentStories.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {currentStories.map((story) => (
                    <button
                      key={story.id}
                      type="button"
                      onClick={() => setSelectedStory(story)}
                      className={`text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all cursor-pointer ${
                        story.featured ? 'md:col-span-2 lg:col-span-1' : ''
                      }`}
                    >
                      {/* Image */}
                      <div className="relative h-48 sm:h-56 overflow-hidden">
                        <img
                          src={story.image || 'https://via.placeholder.com/800x600?text=Vendor+Story'}
                          alt={story.vendor_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/800x600?text=Vendor+Story';
                          }}
                        />
                        {story.featured && (
                          <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            Featured
                          </div>
                        )}
                        <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium capitalize">
                          {story.category}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-4 sm:p-5 md:p-6">
                        <div className="flex items-center gap-2 mb-3">
                          <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500 fill-current" />
                          <span className="text-sm sm:text-base font-semibold text-gray-900">
                            {story.vendor_name}
                          </span>
                        </div>

                        <div className="mb-4">
                          <Quote className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 mb-2" />
                          <p className="text-xs sm:text-sm md:text-base text-gray-700 italic leading-relaxed line-clamp-3">
                            "{truncateQuote(story.quote)}"
                          </p>
                          {story.quote?.length > QUOTE_MAX_LENGTH && (
                            <span className="text-xs text-primary-600 font-medium mt-1 inline-block">
                              Read more →
                            </span>
                          )}
                        </div>

                        <div className="border-t border-gray-200 pt-3 sm:pt-4">
                          <p className="text-xs sm:text-sm font-semibold text-gray-900 mb-1">
                            {story.author}
                          </p>
                          <p className="text-xs text-gray-600 mb-3 sm:mb-4">
                            {story.role}
                          </p>

                          {/* View Vendor - navigate to /vendor/:id */}
                          {story.vendor_id && (
                            <Link
                              to={`/vendor/${story.vendor_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-700 mb-3"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View Vendor
                            </Link>
                          )}

                          {/* Stats */}
                          {story.stats && (story.stats.bookings || story.stats.rating || story.stats.revenue) && (
                            <div className="grid grid-cols-3 gap-2 sm:gap-3">
                              {story.stats.bookings != null && (
                                <div className="text-center">
                                  <div className="text-xs sm:text-sm font-semibold text-gray-900">
                                    {story.stats.bookings}
                                  </div>
                                  <div className="text-xs text-gray-600">Bookings</div>
                                </div>
                              )}
                              {story.stats.rating != null && (
                                <div className="text-center">
                                  <div className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center justify-center gap-1">
                                    <Star className="w-3 h-3 text-yellow-500 fill-current" />
                                    {story.stats.rating}
                                  </div>
                                  <div className="text-xs text-gray-600">Rating</div>
                                </div>
                              )}
                              {story.stats.revenue && (
                                <div className="text-center">
                                  <div className="text-xs sm:text-sm font-semibold text-gray-900">
                                    {story.stats.revenue}
                                  </div>
                                  <div className="text-xs text-gray-600">Revenue</div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 sm:py-12">
                  <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm sm:text-base text-gray-600">
                    No success stories available in this category yet.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-xl p-4 sm:p-6 md:p-8 text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-4 sm:mb-6 max-w-2xl mx-auto">
              Join thousands of vendors who are growing their businesses with VendaList. Start your journey today.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <a
                href="/vendor/subscription"
                className="px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-black rounded-lg sm:rounded-xl font-semibold hover:bg-gray-100 transition-colors text-sm sm:text-base text-center"
              >
                Become a Vendor
              </a>
              <a
                href="/how-it-works"
                className="px-5 sm:px-6 py-2.5 sm:py-3 border-2 border-white text-white rounded-lg sm:rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm sm:text-base text-center"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </div>

      {selectedStory && (
        <StoryDetailModal
          story={selectedStory}
          onClose={() => setSelectedStory(null)}
        />
      )}
    </div>
  );
};

export default VendorSuccessStories;
